import React from 'react'
import { tv } from 'tailwind-variants'
import Button from 'components/button'

// In the manner of shadcn/ui's tabs: the choices side by side in a grey track, the chosen one raised on the
// surface. A tab can carry a count, in a small pill after its label, in the product's blue so it stands out.
const tabs = tv({
  slots: {
    list: 'inline-flex h-8 shrink-0 items-center gap-0.5 rounded-lg bg-surface-sunken p-[3px]',
    tab: 'h-full gap-1.5 rounded-md px-2.5 text-[13px] font-medium hover:bg-transparent',
    count: 'min-w-4 rounded-full bg-blue px-1.5 text-center text-[11px] leading-4 font-medium text-white tabular-nums'
  },
  variants: {
    selected: {
      true: {
        tab: 'bg-surface text-body shadow-[0_1px_2px_rgb(0_0_0/0.08)] hover:bg-surface hover:text-body'
      },
      false: { tab: 'text-muted hover:text-body' }
    }
  }
})

export interface TabOption<T extends string> {
  value: T
  label: string
  /** A count after the label, in a pill: left out when undefined. */
  count?: number
}

interface TabsProps<T extends string> {
  /** What the tabs choose between, for screen readers: "Show". */
  label: string
  options: TabOption<T>[]
  value: T
  onChange: (value: T) => void
}

/**
 * Tabs choosing what's shown, as shadcn/ui's. As in a tab list, Tab reaches the chosen tab and the arrow
 * keys (and Home and End) move between them, choosing as they go.
 */
const Tabs = <T extends string>({ label, options, value, onChange }: TabsProps<T>): React.ReactElement => {
  const move = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = options.findIndex((option) => option.value === value)
    const last = options.length - 1
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last
    }[event.key]
    if (next === undefined) return
    event.preventDefault()
    onChange(options[next].value)
    event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus()
  }

  return (
    <div role="tablist" aria-label={label} className={tabs().list()}>
      {options.map((option) => {
        const styles = tabs({ selected: option.value === value })
        return (
          <Button
            key={option.value}
            internalType="transparent"
            role="tab"
            aria-selected={option.value === value}
            tabIndex={option.value === value ? 0 : -1}
            className={styles.tab()}
            onClick={() => onChange(option.value)}
            onKeyDown={move}
          >
            {option.label}
            {/* A space, which the flex layout ignores, so it's read "Invited 6". */}
            {option.count !== undefined && ' '}
            {option.count !== undefined && <span className={styles.count()}>{option.count}</span>}
          </Button>
        )
      })}
    </div>
  )
}

export default Tabs
